import React, { useEffect, useState } from "react";
import styled from "styled-components";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import AdminShell from "./AdminShell";
import { firestore } from "../firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

/* ---------- UI (nya designen) ---------- */

const Grid = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: 1fr 1fr;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  padding: 14px;
  background: #fff;
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.06);
`;

const H3 = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 950;
  color: #111827;
`;

const Muted = styled.div`
  margin-top: 8px;
  color: #6b7280;
  font-weight: 800;
  font-size: 13px;
  line-height: 1.5;
`;

const BtnRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 12px;
`;

const Btn = styled.button`
  appearance: none;
  border: 1px solid rgba(15, 118, 110, 0.22);
  background: #0f766e;
  color: #fff;
  font-weight: 950;
  border-radius: 999px;
  padding: 10px 14px;
  cursor: pointer;

  &:disabled {
    opacity: 0.65;
    cursor: wait;
    transform: none;
  }

  &:hover {
    filter: brightness(1.03);
    transform: translateY(-1px);
  }
`;

const Ghost = styled(Btn)`
  background: #fff;
  color: #111827;
  border: 1px solid #e5e7eb;

  &:hover {
    background: #f9fafb;
  }
`;

const PreviewBox = styled.div`
  margin-top: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 14px;
  background: #fafafa;
`;

const PreviewHtml = styled.div`
  color: #111827;
  font-weight: 700;
  line-height: 1.65;

  p {
    margin: 0 0 10px;
  }
  a {
    color: #0f766e;
    font-weight: 900;
  }
  h1, h2, h3 {
    margin: 10px 0 10px;
  }
  ul, ol {
    margin: 8px 0 8px 18px;
  }
`;

const QuillWrap = styled.div`
  margin-top: 12px;

  .ql-toolbar.ql-snow {
    border-radius: 16px 16px 0 0;
    border-color: #e5e7eb;
    background: #fff;
  }

  .ql-container.ql-snow {
    border-radius: 0 0 16px 16px;
    border-color: #e5e7eb;
    min-height: 260px;
    background: #fff;
  }

  .ql-editor {
    font-weight: 700;
    color: #111827;
    line-height: 1.65;
  }
`;

/* ---------- Component ---------- */

export default function AdminForening() {
  const DOC_REF = doc(firestore, "föreningstext", "content");

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [foreningText, setForeningText] = useState("");
  const [styrelseText, setStyrelseText] = useState("");

  const [editForening, setEditForening] = useState(false);
  const [editStyrelse, setEditStyrelse] = useState(false);

  const [editorForening, setEditorForening] = useState("");
  const [editorStyrelse, setEditorStyrelse] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const snap = await getDoc(DOC_REF);
        if (snap.exists()) {
          const d = snap.data() || {};
          const ft = d.föreningText || "";
          const st = d.styrelseText || "";

          setForeningText(ft);
          setStyrelseText(st);

          setEditorForening(ft);
          setEditorStyrelse(st);
        } else {
          setForeningText("");
          setStyrelseText("");
          setEditorForening("");
          setEditorStyrelse("");
        }
      } finally {
        setLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveForening = async () => {
    setBusy(true);
    try {
      await setDoc(
        DOC_REF,
        {
          föreningText: editorForening,
          styrelseText, // behåll nuvarande
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      setForeningText(editorForening);
      setEditForening(false);
      alert("Sparat!");
    } catch (e) {
      alert(e?.message || "Kunde inte spara föreningstext.");
    } finally {
      setBusy(false);
    }
  };

  const saveStyrelse = async () => {
    setBusy(true);
    try {
      await setDoc(
        DOC_REF,
        {
          foreningText, // behåll nuvarande
          styrelseText: editorStyrelse,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      setStyrelseText(editorStyrelse);
      setEditStyrelse(false);
      alert("Sparat!");
    } catch (e) {
      alert(e?.message || "Kunde inte spara styrelsetext.");
    } finally {
      setBusy(false);
    }
  };

  const cancelForening = () => {
    setEditorForening(foreningText || "");
    setEditForening(false);
  };

  const cancelStyrelse = () => {
    setEditorStyrelse(styrelseText || "");
    setEditStyrelse(false);
  };

  return (
    <AdminShell title="Föreningen">
      <Grid>
        {/* Föreningens text */}
        <Card>
          <H3>Föreningens text</H3>
          <Muted>
            Firestore: <code>föreningstext/content</code> → <b>föreningText</b>
          </Muted>

          {loading ? (
            <Muted style={{ marginTop: 12 }}>Laddar…</Muted>
          ) : editForening ? (
            <QuillWrap>
              <ReactQuill
                value={editorForening}
                onChange={setEditorForening}
                theme="snow"
                modules={{
                  toolbar: [
                    [{ header: "1" }, { header: "2" }, { font: [] }],
                    [{ list: "ordered" }, { list: "bullet" }],
                    ["bold", "italic", "underline"],
                    ["link"],
                    ["blockquote"],
                    [{ align: [] }],
                    ["image"],
                    ["clean"],
                  ],
                }}
              />
            </QuillWrap>
          ) : (
            <PreviewBox>
              {foreningText ? (
                <PreviewHtml dangerouslySetInnerHTML={{ __html: foreningText }} />
              ) : (
                <Muted>Ingen text sparad ännu.</Muted>
              )}
            </PreviewBox>
          )}

          <BtnRow>
            {editForening ? (
              <>
                <Btn type="button" onClick={saveForening} disabled={busy}>
                  {busy ? "Sparar…" : "Spara"}
                </Btn>
                <Ghost type="button" onClick={cancelForening} disabled={busy}>
                  Avbryt
                </Ghost>
              </>
            ) : (
              <Btn type="button" onClick={() => setEditForening(true)} disabled={loading}>
                Redigera
              </Btn>
            )}
          </BtnRow>
        </Card>

        {/* Styrelsens text */}
        <Card>
          <H3>Styrelsens text</H3>
          <Muted>
            Firestore: <code>föreningstext/content</code> → <b>styrelseText</b>
          </Muted>

          {loading ? (
            <Muted style={{ marginTop: 12 }}>Laddar…</Muted>
          ) : editStyrelse ? (
            <QuillWrap>
              <ReactQuill
                value={editorStyrelse}
                onChange={setEditorStyrelse}
                theme="snow"
                modules={{
                  toolbar: [
                    [{ header: "1" }, { header: "2" }, { font: [] }],
                    [{ list: "ordered" }, { list: "bullet" }],
                    ["bold", "italic", "underline"],
                    ["link"],
                    ["blockquote"],
                    [{ align: [] }],
                    ["image"],
                    ["clean"],
                  ],
                }}
              />
            </QuillWrap>
          ) : (
            <PreviewBox>
              {styrelseText ? (
                <PreviewHtml dangerouslySetInnerHTML={{ __html: styrelseText }} />
              ) : (
                <Muted>Ingen text sparad ännu.</Muted>
              )}
            </PreviewBox>
          )}

          <BtnRow>
            {editStyrelse ? (
              <>
                <Btn type="button" onClick={saveStyrelse} disabled={busy}>
                  {busy ? "Sparar…" : "Spara"}
                </Btn>
                <Ghost type="button" onClick={cancelStyrelse} disabled={busy}>
                  Avbryt
                </Ghost>
              </>
            ) : (
              <Btn type="button" onClick={() => setEditStyrelse(true)} disabled={loading}>
                Redigera
              </Btn>
            )}
          </BtnRow>
        </Card>
      </Grid>
    </AdminShell>
  );
}










